import { useLayoutEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import './code-entry.css';

/** The number of digits in a code. */
export const CODE_LENGTH = 6;

/** Props for `CodeEntry`. */
export interface CodeEntryProps {
  /** The digits typed so far (up to six). */
  value: string;
  onChange: (value: string) => void;
  /** Called once all six digits are in. */
  onComplete: (code: string) => void;
  /** The ID of the element that labels the group. */
  labelledBy: string;
  /** The ID of the error or hint describing the group. */
  describedBy?: string;
  invalid?: boolean;
  disabled?: boolean;
}

/** Keeps the digits of `text`, at most `max` of them. */
export function digitsOf(text: string, max = CODE_LENGTH): string {
  return text.replace(/\D/g, '').slice(0, max);
}

/**
 * Six boxes for a one-time code. Typing moves to the next box, Backspace
 * goes back, the arrow keys move, and pasting (or the phone's one-time-code
 * autofill) fills every box at once. Each box pops gently as it fills
 * (`motion-pop`; with reduced motion it simply fills).
 *
 * Plain inputs, not React Aria, so the sign-in page stays within the public
 * JavaScript budget; they are labelled as a group and one by one.
 *
 * @example
 * <CodeEntry value={code} onChange={setCode} onComplete={submit} labelledBy="code-label" />
 */
export function CodeEntry({
  value,
  onChange,
  onComplete,
  labelledBy,
  describedBy,
  invalid = false,
  disabled = false,
}: CodeEntryProps) {
  const { t } = useTranslation();
  const boxes = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: CODE_LENGTH }, (_, index) => value[index] ?? '');

  // Also restore focus after an incorrect code clears/re-enables the boxes.
  // Layout timing avoids a frame with focus on the previous screen's control.
  useLayoutEffect(() => {
    if (!disabled && value === '') boxes.current[0]?.focus({ preventScroll: true });
  }, [disabled, value]);

  const focus = (index: number) => {
    const box = boxes.current[Math.max(0, Math.min(CODE_LENGTH - 1, index))];
    box?.focus();
    box?.select();
  };

  /** Writes the digits of `typed` from box `index` onwards (the code has no gaps). */
  const fill = (index: number, typed: string) => {
    const start = Math.min(index, value.length);
    const incoming = digitsOf(typed, CODE_LENGTH - start);
    if (!incoming) return;
    const merged = digitsOf(
      value.slice(0, start) + incoming + value.slice(start + incoming.length),
    );
    onChange(merged);
    if (merged.length === CODE_LENGTH) {
      boxes.current[CODE_LENGTH - 1]?.blur();
      onComplete(merged);
    } else {
      focus(start + incoming.length);
    }
  };

  const onInput = (index: number) => (event: { currentTarget: HTMLInputElement }) => {
    const typed = event.currentTarget.value;
    const old = digits[index] ?? '';
    if (typed === '') {
      onChange(value.slice(0, index) + value.slice(index + 1));
      return;
    }
    // A box that held a digit receives the old digit plus the new one.
    fill(index, old && typed.length === 2 ? typed.replace(old, '') : typed);
  };

  const onKeyDown = (index: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      event.preventDefault();
      onChange(value.slice(0, index - 1));
      focus(index - 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focus(index - 1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      focus(index + 1);
    }
  };

  const onPaste = (index: number) => (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    fill(index, event.clipboardData.getData('text'));
  };

  return (
    <div
      className="code-entry"
      role="group"
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      data-invalid={invalid || undefined}
    >
      {digits.map((digit, index) => (
        <div
          // The boxes are positional: their index is their identity.
          key={index}
          className={`code-entry__box${digit ? ' code-entry__box--filled motion-pop' : ''}`}
          data-disabled={disabled || undefined}
        >
          <input
            ref={(element) => {
              boxes.current[index] = element;
            }}
            className="code-entry__input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            autoFocus={index === 0}
            maxLength={index === 0 ? CODE_LENGTH : 1}
            value={digit}
            disabled={disabled}
            aria-label={t('signIn.code.digit', { index: index + 1, count: CODE_LENGTH })}
            aria-invalid={invalid || undefined}
            onInput={onInput(index)}
            onKeyDown={onKeyDown(index)}
            onPaste={onPaste(index)}
            onFocus={(event) => {
              event.currentTarget.select();
            }}
          />
          <span className="code-entry__digit" aria-hidden="true">
            {digit}
          </span>
        </div>
      ))}
    </div>
  );
}
